import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import styled from '@emotion/styled';
import css from '@emotion/css';
import song from '../utils/song';

import {
  StyledServiceWrapper,
  BottomContainer,
  BottomContents,
} from '../components/SectionPage';
import NotePlayer from '../components/NotePlayer';
import { INoteData, initialNoteData } from '../components/NotePlayer/types';

import { api } from '../utils/api';
import { roomFromUrl } from '../components/NotePlayer/ensemble';
import { device, track } from '../utils/track';

import { RecoilRoot } from 'recoil';

const Notes: React.FC = () => {
  const { songIdx } = useParams<{ songIdx: string }>();
  const history = useHistory();
  const [noteData, setNoteData] = useState<INoteData>(initialNoteData);
  // 동시에 보기 2대까지 (2026-10-10): 다른 기기에서 보는 중이면 여기서 보기를 고르게
  const [blocked, setBlocked] = useState<{ code: string; devices: { name: string; minutes: number }[] } | null>(null);

  const fetchData = async (take = false) => {
    try {
      const { data: res } = await api.get('/score/detail', {
        params: { id: songIdx, room: roomFromUrl() || undefined, device: device() || undefined, take: take ? 1 : undefined }, // 합주방 링크면 room (방장이 구독 회원이면 무료로 봄)
      });
      if (res && res.success === false && (res.code === 'DEVICE_LIMIT' || res.code === 'KICKED')) {
        setBlocked({ code: res.code, devices: (res.data && res.data.devices) || [] });
        return;
      }
      setBlocked(null);
      setNoteData(res.data);
    } catch (err) { }
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songIdx]);

  // 보는 동안 1분마다 '보는 중' 알림 → 다른 기기가 '여기서 보기'를 누르면 여기는 멈춤
  useEffect(() => {
    if (blocked || !noteData.info) return;
    const t = setInterval(async () => {
      try {
        const { data: res } = await api.post('/view/ping', { device: device() });
        if (res && res.success === false && res.code) setBlocked({ code: res.code, devices: (res.data && res.data.devices) || [] });
      } catch (e) {}
    }, 60000);
    return () => clearInterval(t);
  }, [blocked, noteData.info]);

  if (blocked) {
    return (
      <DeviceLimit>
        <div>
          <h2>{blocked.code === 'KICKED' ? '다른 기기에서 보기 시작했어요' : '다른 기기에서 보는 중이에요'}</h2>
          <p>
            {blocked.code === 'KICKED'
              ? '한 계정으로 동시에 2대까지 볼 수 있어서, 여기서는 악보 보기를 멈췄어요.'
              : '한 계정으로 동시에 2대까지 볼 수 있어요. 여기서 보면 가장 오래 안 본 기기는 멈춰요.'}
          </p>
          {blocked.devices.length > 0 && (
            <ul>
              {blocked.devices.map((d, i) => (
                <li key={i}>{d.name} · {d.minutes ? `${d.minutes}분 전` : '지금'} 보는 중</li>
              ))}
            </ul>
          )}
          <button type="button" className="primary" onClick={() => { track('view:take'); fetchData(true); }}>여기서 보기</button>
          <button type="button" onClick={() => history.goBack()}>돌아가기</button>
          <small>여러 사람이 같이 보려면 악보 위 사람 모양 버튼(합주방)으로 링크를 보내 주세요.</small>
        </div>
      </DeviceLimit>
    );
  }

  return <NoteStage noteData={noteData} />;
};

const DeviceLimit = styled.div`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  & > div {
    width: 100%;
    max-width: 420px;
    padding: 24px 20px;
    border-radius: 20px;
    background: #16181e;
    border: 1px solid #3a3220;
    color: #f3f4f6;
    display: flex;
    flex-direction: column;
    gap: 12px;
    word-break: keep-all;
  }
  & h2 {
    margin: 0;
    font-size: 20px;
  }
  & p {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: #c7cbd3;
  }
  & ul {
    margin: 0;
    padding: 10px 14px;
    list-style: none;
    border-radius: 12px;
    background: #1f222a;
    font-size: 14px;
    color: #d5d9e0;
  }
  & button {
    height: 50px;
    border-radius: 14px;
    border: 1px solid #3a3f4b;
    background: transparent;
    color: #e9ebf0;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
  }
  & button.primary {
    border: 0;
    background: #ffb547;
    color: #0d0e12;
    font-weight: 800;
  }
  & small {
    font-size: 12px;
    color: #a3a9b5;
    line-height: 1.5;
  }
`;

export default Notes;

/** 악보 보기 화면 틀 (공개 악보 /notes/:id 와 내 악보 /my-scores/:id 가 같이 씀). top: 악보 위에 붙일 줄(내 악보의 고치기·공유 등) */
export const NoteStage: React.FC<{ noteData: INoteData; top?: React.ReactNode }> = ({ noteData, top }) => {
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const history = useHistory();

  // 악보 영역 높이 = 지금 보이는 화면(innerHeight)에서 악보 영역 윗부분까지를 뺀 나머지.
  // 예전엔 화면 전체 크기(screen.availHeight)에서 고정값을 빼서, 가로 모드·앱에서 실제 보이는 영역과 어긋나
  // 스크롤이 안 되거나 아래가 잘렸다.
  const bottomRef = useRef<HTMLDivElement>(null);
  const [areaHeight, setAreaHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const measure = () => {
      const el = bottomRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setAreaHeight(Math.max(240, Math.floor(window.innerHeight - top)));
    };
    measure();
    const later = () => setTimeout(measure, 300); // 회전 직후에는 크기가 늦게 바뀌는 기기가 있다
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', later);
    // 처음 잴 때 위쪽(상단바·도구줄)이 아직 덜 그려져 있으면 악보 영역이 화면보다 길어졌다(아이패드 23px)
    // → 위쪽 크기가 바뀔 때마다 다시 잰다. 같은 값이면 다시 그리지 않으므로 무한 반복 없음 (2026-10-12)
    const RO = (window as any).ResizeObserver;
    const ro = RO ? new RO(measure) : null;
    if (ro) {
      const header = document.getElementById('header');
      if (header) ro.observe(header);
      const prev = bottomRef.current && bottomRef.current.previousElementSibling;
      if (prev) ro.observe(prev);
    }
    const t = setTimeout(measure, 1200);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', later);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
  }, [isFullScreen, noteData.info]);

  useEffect(() => {
    // return () => {
    //   if (history.action === "POP") {
    //     const songId = song.getSelectedSong();
    //     if (!songId) return;

    //     const type = songId.split('-')[0];
    //     if (type == 'new')
    //       history.push('/recent');
    //     else if (type == 'rank')
    //       history.push('/chart');
    //   }
    // };
  }, [history])

  window.addEventListener('popstate', (event) => {
    if (window.chordscore) {
      window.chordscore.postMessage('showNavigationBar');
    }
  });

  return (
    <RecoilRoot>
      <NoteServiceWrapper isFooterRendered={false}>
        {top}
        <StyledBottomContainer
          ref={bottomRef}
          areaHeight={areaHeight}
          isFullScreen={isFullScreen}
        >
          <StyledBottomContents>
            {noteData.info && <NotePlayer handleFullScreen={(isFullScreen) => setIsFullScreen(isFullScreen)} data={noteData} />}
          </StyledBottomContents>
        </StyledBottomContainer>
      </NoteServiceWrapper>
    </RecoilRoot>
  );
};

const NoteServiceWrapper = styled(StyledServiceWrapper)`
  /* 악보 영역 높이는 화면에 딱 맞춰 따로 잰다. 기본 최소 높이(화면 - 75px)는 실제 상단바(85~98px)보다 작게 빼서
     화면이 10~23px 길어지고, 아이패드에서 끌면 화면 전체가 밀렸다 (2026-10-12) */
  min-height: 0;

  @media screen and (max-width: 1300px) {
    width: 92%;
    margin: 0 auto;
  }

  @media screen and (max-width: 585px) {
    width: 94%;
  }
`;

interface IAreaHeight {
  areaHeight?: number;
  isFullScreen?: boolean;
}

const StyledBottomContainer = styled(BottomContainer) <IAreaHeight>`
  display: flex;
  flex: none;
  height: calc(100vh - 200px);

  ${({ areaHeight }) =>
    areaHeight &&
    css`
      height: ${areaHeight}px;
    `};

  @media screen and (max-width: 1300px) {
    padding: 0;
    margin-top: 32px;
  }

  @media screen and (max-width: 585px) {
    margin-top: 16px;
    ${({ isFullScreen }) =>
    isFullScreen &&
    css`
      margin-top: 40px;
   `};
  }
`;

const StyledBottomContents = styled(BottomContents)`
  padding-top: 56px;
  padding-bottom: 0;
  max-width: 1400px;
  height: auto;

  @media screen and (max-width: 1300px) {
    width: 100%;
    padding: 0;
  }

  @media screen and (max-width: 500px) {
    margin-top: 0;
  }
`;
